if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        const prevButton = this.querySelector('.product-media-modal__prev');
        const nextButton = this.querySelector('.product-media-modal__next');
        if (prevButton) prevButton.addEventListener('click', this.showPreviousMedia.bind(this));
        if (nextButton) nextButton.addEventListener('click', this.showNextMedia.bind(this));

        // Let the left/right arrow keys move through the images while the
        // full-screen viewer is open (ignored while it's closed since this
        // element has no 'open' attribute then).
        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showNextMedia();
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showPreviousMedia();
          }
        });

        const content = this.querySelector('[role="document"]');
        if (content) {
          let touchStartX = 0;
          let touchStartY = 0;

          content.addEventListener(
            'touchstart',
            (event) => {
              touchStartX = event.touches[0].clientX;
              touchStartY = event.touches[0].clientY;
            },
            { passive: true }
          );

          content.addEventListener(
            'touchend',
            (event) => {
              const touchEndX = event.changedTouches[0].clientX;
              const touchEndY = event.changedTouches[0].clientY;
              const deltaX = touchEndX - touchStartX;
              const deltaY = touchEndY - touchStartY;

              if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
                if (deltaX < 0) {
                  this.showNextMedia();
                } else {
                  this.showPreviousMedia();
                }
              }
            },
            { passive: true }
          );

          // Mouse drag support (click and drag to change image, same behavior as the touch swipe above)
          let mouseStartX = 0;
          let mouseStartY = 0;
          let isMouseDragging = false;

          content.addEventListener('mousedown', (event) => {
            isMouseDragging = true;
            mouseStartX = event.clientX;
            mouseStartY = event.clientY;
            content.classList.add('product-media-modal__content--dragging');
          });

          const endMouseDrag = (event) => {
            if (!isMouseDragging) return;
            isMouseDragging = false;
            content.classList.remove('product-media-modal__content--dragging');

            const mouseEndX = event.clientX;
            const mouseEndY = event.clientY;
            const deltaX = mouseEndX - mouseStartX;
            const deltaY = mouseEndY - mouseStartY;

            if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
              if (deltaX < 0) {
                this.showNextMedia();
              } else {
                this.showPreviousMedia();
              }
            }
          };

          content.addEventListener('mouseup', endMouseDrag);
          content.addEventListener('mouseleave', () => {
            isMouseDragging = false;
            content.classList.remove('product-media-modal__content--dragging');
          });
          content.addEventListener('dragstart', (event) => event.preventDefault());

          // Two-finger trackpad swipe support. A single swipe fires many
          // small wheel events, so movement is accumulated until it crosses
          // a threshold, then a short cooldown stops one swipe from flipping
          // through several images at once.
          let wheelDeltaX = 0;
          let wheelLocked = false;
          let wheelResetTimer;

          content.addEventListener(
            'wheel',
            (event) => {
              if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
              event.preventDefault();

              // While locked, ignore events completely (don't even
              // accumulate) — otherwise the trailing "momentum" events a
              // trackpad keeps sending after you lift your fingers pile up
              // during the lock and fire an extra step the instant it lifts.
              if (wheelLocked) return;

              wheelDeltaX += event.deltaX;
              window.clearTimeout(wheelResetTimer);
              wheelResetTimer = window.setTimeout(() => {
                wheelDeltaX = 0;
              }, 150);

              if (Math.abs(wheelDeltaX) < 50) return;

              if (wheelDeltaX > 0) {
                this.showNextMedia();
              } else {
                this.showPreviousMedia();
              }

              wheelDeltaX = 0;
              wheelLocked = true;
              window.clearTimeout(wheelResetTimer);
              window.setTimeout(() => {
                wheelLocked = false;
              }, 600);
            },
            { passive: false }
          );
        }
      }
      hide() {
        super.hide();
        if (this.zoomHistoryPushed) {
          this.zoomHistoryPushed = false;
          this.ignoreZoomPop = true;
          history.back();
        }
     }

      show(opener) {
        super.show(opener);
        if (!this.zoomHistoryPushed) {
          history.pushState({ lumaZoom: 1 }, '');
          this.zoomHistoryPushed = true;
          if (!this.zoomPopBound) {
            this.zoomPopBound = true;
            window.addEventListener('popstate', () => {
              if (this.ignoreZoomPop) { this.ignoreZoomPop = false; return; }
              if (this.zoomHistoryPushed) {
                this.zoomHistoryPushed = false;
                this.hide();
              }
            });
          }
        }
        this.showActiveMedia();
        this.scrollToActiveMedia();
      }

      scrollToActiveMedia() {
        const content = this.querySelector('[role="document"]');
        if (!content || window.innerWidth > 749) return;
        const items = this.getMediaItems();
        const index = items.findIndex((item) => item.classList.contains('active'));
        if (index < 0) return;
        content.style.scrollSnapType = 'none';
        content.scrollLeft = index * content.clientWidth;
        window.setTimeout(() => { content.style.scrollSnapType = ''; }, 50);
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('[role="document"] > [data-media-id]'));
      }

      activateMedia(activeMedia) {
        if (!activeMedia) return;

        this.getMediaItems().forEach((element) => {
          if (element !== activeMedia) element.classList.remove('active');
        });

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      showActiveMedia() {
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        this.activateMedia(activeMedia);
      }

      showNextMedia() {
        const items = this.getMediaItems();
        if (items.length < 2) return;
        const currentIndex = items.findIndex((item) => item.classList.contains('active'));
        const nextIndex = (currentIndex + 1) % items.length;
        this.activateMedia(items[nextIndex]);
      }

      showPreviousMedia() {
        const items = this.getMediaItems();
        if (items.length < 2) return;
        const currentIndex = items.findIndex((item) => item.classList.contains('active'));
        const previousIndex = (currentIndex - 1 + items.length) % items.length;
        this.activateMedia(items[previousIndex]);
      }
    }
  );
}
